import { useCallback, useContext, useMemo, useState } from 'react';
import type { ActionMeta, MultiValue } from 'react-select';
import styled from '@emotion/styled';
import {
  ProjectClipsFiltersContext,
  SetProjectClipsFiltersContext,
} from '@trinity/chat-components/contexts/project-clips';
import { ProjectTagsQueryContext } from '@trinity/chat-components/contexts/project-tags';
import { Button } from '@trinity/chat-components/components/presentation/Button/index.js';
import { TagsMultiSelect } from '@/components/Transcript/Tag.MultiSelect';
import type { TagsMultiselectItem } from '@/components/Transcript/Tag.MultiSelect';

type Props = {
  onSubmit: () => void;
};

export const TagsFilterMenu = ({ onSubmit }: Props) => {
  const filters = useContext(ProjectClipsFiltersContext);
  const setFilters = useContext(SetProjectClipsFiltersContext);
  const tagsQuery = useContext(ProjectTagsQueryContext);

  const [selectedIds, setSelectedIds] = useState<number[]>(filters.tagIds);
  const [inputValue, setInputValue] = useState('');

  const options: TagsMultiselectItem[] = useMemo(() => {
    const tags = tagsQuery?.data?.tags ?? [];
    return tags.map((tag) => ({
      color: tag.base.color,
      id: tag.base.id,
      name: tag.base.name,
      typeId: tag.base.typeId,
    }));
  }, [tagsQuery?.data?.tags]);

  const selected = useMemo(() => options.filter((o) => selectedIds.includes(o.id)), [options, selectedIds]);

  const noOptionsMessage = useCallback(
    (obj: { inputValue: string }) => {
      if (tagsQuery?.isFetching) return 'Loading...';
      return obj.inputValue?.length ? 'No tags found' : null;
    },
    [tagsQuery?.isFetching]
  );

  const handleChange = useCallback(
    (value: MultiValue<TagsMultiselectItem>, _meta: ActionMeta<TagsMultiselectItem>) => {
      setSelectedIds(value.map((v) => v.id));
    },
    []
  );

  const handleInputChange = useCallback((v: string) => setInputValue(v), []);

  const handleSubmit = useCallback(() => {
    setFilters({ tagIds: selected.map((s) => s.id) });
    onSubmit();
  }, [onSubmit, selected, setFilters]);

  return (
    <Menu>
      <TagsMultiSelect
        selected={selected}
        inputValue={inputValue}
        options={options}
        noOptionsMessage={noOptionsMessage}
        onChange={handleChange}
        onInputChange={handleInputChange}
      />
      <Footer>
        <Button size="small" variant="brick" color="primary" onClick={handleSubmit}>
          Apply
        </Button>
      </Footer>
    </Menu>
  );
};

TagsFilterMenu.displayName = 'Project.Highlights.TagsFilterMenu';

const Menu = styled.div`
  background-color: var(--pri-01);
  border: 1px solid var(--gray-l);
  border-radius: 5px;
  box-shadow: 0 2px 6px 2px #3c404326;
  box-sizing: border-box;
  padding: 12px;
  width: 375px;
`;

const Footer = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
`;
